import { calculateKillBounty } from './bounties.js';

// Sistema de Oro, Recompensas por Asesinato (Last Hit) y Experiencia (Paso 39)

export const LEVEL_EXP_TABLE = [
  0,     // Level 1
  280,   // Level 2
  660,   // Level 3 (280 + 380)
  1140,  // Level 4
  1720,  // Level 5
  2400   // Level 6 (Ultimate Upgrade)
];

/** Season 2 assist pool shared equally among champion assisters. */
export const ASSIST_GOLD_POOL = 150;
/** ARAM 5v5: at most 4 allies can share assist gold (excludes killer). */
export const MAX_ASSIST_RECIPIENTS = 4;

export class GoldExpSystem {
  static resolveKillBounty(victimEntity) {
    if (!victimEntity) return 0;
    if (victimEntity.type === 'champion') {
      if (victimEntity.bounty === 0) return 0;
      return calculateKillBounty(
        victimEntity.killStreak || 0,
        victimEntity.deathStreak || 0,
        !!(victimEntity.isFirstBloodVictim || victimEntity.firstBlood)
      );
    }
    if (victimEntity.bounty !== undefined) return victimEntity.bounty;
    return 21;
  }

  // Aplicar Recompensa de Oro por Último Golpe (Last Hit)
  static awardKillBounty(killerEntity, victimEntity) {
    if (!killerEntity || !victimEntity || killerEntity.isDead) return 0;
    if (victimEntity.bountyProcessed) return 0;
    victimEntity.bountyProcessed = true;

    let goldAwarded = 0;
    const bounty = GoldExpSystem.resolveKillBounty(victimEntity);

    if (killerEntity.type === 'champion') {
      killerEntity.gold = (killerEntity.gold || 0) + bounty;
      goldAwarded = bounty;
    }

    return goldAwarded;
  }

  /**
   * Equal-share assist gold with floor clamp (SPY-203).
   * Pool capped at ASSIST_GOLD_POOL; recipients capped at MAX_ASSIST_RECIPIENTS.
   * SPY-403: leftover after floor(share) goes to the first living assister.
   */
  static awardAssistGold(assisters = [], totalGold = ASSIST_GOLD_POOL) {
    const pool = Math.min(Math.max(0, Number(totalGold) || 0), ASSIST_GOLD_POOL);
    const recipients = (assisters || [])
      .filter((a) => a && a.type === 'champion' && !a.isDead)
      .slice(0, MAX_ASSIST_RECIPIENTS);
    if (recipients.length === 0 || pool <= 0) return 0;
    const share = Math.floor(pool / recipients.length);
    if (share <= 0) return 0;
    const remainder = pool - share * recipients.length;
    let awarded = 0;
    for (let i = 0; i < recipients.length; i += 1) {
      const extra = i === 0 ? remainder : 0;
      const amount = share + extra;
      recipients[i].gold = (recipients[i].gold || 0) + amount;
      awarded += amount;
    }
    return awarded;
  }

  // Otorgar Experiencia a Campeones Cercanos (Radio de Exp = 8.0u)
  static awardNearbyExp(alliedChampions = [], victimEntity, radius = 8.0) {
    if (!victimEntity || victimEntity.nearbyExpProcessed) return 0;

    const tableValue = {
      minion_melee: 59,
      minion_ranged: 29,
      minion_cannon: 92,
      champion: 210
    }[victimEntity.type];
    let expValue;
    if (victimEntity.type === 'champion') {
      expValue = 210;
    } else if (tableValue !== undefined) {
      expValue = tableValue;
    } else if (victimEntity.exp > 0) {
      expValue = victimEntity.exp;
    } else {
      expValue = 30;
    }

    const nearbyChampions = [];
    const seen = new Set();
    for (const c of alliedChampions || []) {
      if (!c || c.isDead || c.team === victimEntity.team) continue;
      if (c.type !== 'champion') continue;
      if (seen.has(c)) continue;
      const dist = Math.hypot(c.x - victimEntity.x, c.z - victimEntity.z);
      if (dist > radius) continue;
      seen.add(c);
      nearbyChampions.push(c);
    }

    if (nearbyChampions.length === 0) return 0;

    victimEntity.nearbyExpProcessed = true;
    const expPerChamp = Math.floor(expValue / nearbyChampions.length);
    const remainder = expValue - expPerChamp * nearbyChampions.length;
    nearbyChampions.forEach((c, i) => {
      const amount = expPerChamp + (i === 0 ? remainder : 0);
      if (typeof c.addExperience === 'function') c.addExperience(amount);
      else c.exp = (c.exp || 0) + amount;
    });
    return nearbyChampions.length;
  }

  // Generación Pasiva de Oro (map tickRate, default 2.5g/s)
  static applyPassiveGold(championEntity, dt, tickRate = 2.5) {
    if (!championEntity || championEntity.isDead) return 0;
    const goldEarned = tickRate * dt;
    championEntity.gold = (championEntity.gold || 0) + goldEarned;
    return goldEarned;
  }

  /** AR-R40: accelerated passive XP (map tickRate XP/s; 0 = off). */
  static applyPassiveXp(championEntity, dt, tickRate = 0) {
    if (!championEntity || championEntity.isDead) return 0;
    if (!Number.isFinite(tickRate) || tickRate <= 0) return 0;
    const xpEarned = tickRate * dt;
    if (typeof championEntity.addExperience === 'function') {
      championEntity.addExperience(xpEarned);
    } else {
      championEntity.exp = (championEntity.exp || 0) + xpEarned;
    }
    return xpEarned;
  }
}
